import { IconArrowBackUp, IconArrowUp } from '@tabler/icons-react'
import { AnimatePresence, motion } from 'motion/react'
import { useRef, useState } from 'react'
import Select, { components, SelectInstance } from 'react-select'

import { AstraButton, AstraIcon, AstraParagraph, astraTwMerge, isShortcutPressed } from '~/astra'
import { trackUserEvent } from '~/utils/analytics'

import { CommandPaletteOption } from './CommandPaletteOption'
import { CommandAction, CommandActionGroup } from './types'

const getActionCategory = (actionId: string) => {
    if (actionId.startsWith('practitioner')) return 'showPractitioner'
    if (actionId.startsWith('privacy')) return 'togglePrivacyMode'
    if (actionId.startsWith('recurring')) return 'staffingRecurringAssignments'
    if (actionId.startsWith('operational')) return 'changeOPView'
    if (actionId.startsWith('staffing-sidebar')) return 'openStaffingDrawer'
    if (actionId.startsWith('staffing-view')) return 'changeStaffingView'
    if (actionId.startsWith('navigation')) return 'navigateToPage'
    return 'unknown'
}

type Props = {
    commands: CommandActionGroup[]
    onClose?: () => void
}

export const CommandPalette = ({ commands, onClose }: Props) => {
    const [inputValue, setInputValue] = useState('')

    const inputRef = useRef<HTMLInputElement>(null)
    const selectInstanceRef = useRef<SelectInstance<CommandAction, false, CommandActionGroup>>(null)

    const handleExecuteCommand = async (action: CommandAction | null) => {
        if (!action) return

        try {
            const shouldClose = (await action.onExecute?.()) ?? true
            void trackUserEvent('COMMAND_PALETTE_USED', { actionCategory: getActionCategory(action.id), hasActiveSearch: inputValue.length > 0 })
            if (shouldClose) {
                onClose?.()
            }
        } catch (error) {
            console.error('Failed to execute command:', error)
        }
    }

    // show only 5 max options per group when no input is present
    const filteredCommands = inputValue.length < 1 ? commands.map(group => ({ ...group, options: group.options.slice(0, 5) })) : commands

    return (
        <>
            <Select<CommandAction, false, CommandActionGroup>
                menuIsOpen
                menuPlacement="bottom"
                options={filteredCommands}
                escapeClearsValue={false}
                openMenuOnFocus
                tabSelectsValue={false}
                blurInputOnSelect={false}
                value={null}
                ref={selectInstanceRef}
                onChange={action => {
                    void handleExecuteCommand(action)
                }}
                filterOption={(option, inputValue) => {
                    const searchLower = inputValue.toLowerCase()
                    const labelMatch = option.label.toLowerCase().includes(searchLower)
                    const descriptionMatch = option.data.description?.toLowerCase().includes(searchLower)
                    const keywordsMatch = option.data.keywords?.some(keyword => keyword.toLowerCase().includes(searchLower))
                    return labelMatch || descriptionMatch || keywordsMatch || false
                }}
                onKeyDown={event => {
                    if (event.key === 'Escape') {
                        onClose?.()
                        return
                    }
                    if (event.key === 'Tab') {
                        // let tab work like arrow-keys
                        const isShift = event.shiftKey
                        const direction = isShift ? 'up' : 'down'
                        selectInstanceRef.current?.focusOption(direction)
                        event.preventDefault()
                    }
                    // block other keys such that other content dont react to eg. arrows-keys
                    // however let CMD+K bubble up, so that palette can be closed
                    if (!isShortcutPressed(event, { keyboardKey: 'K', hasMeta: true })) {
                        event.stopPropagation()
                    }
                }}
                inputValue={inputValue}
                onInputChange={(value, actionMeta) => {
                    // prevent flicker from search being cleared when option is selected
                    // this is due to setInputValue being called with '' and action === 'set-value' when option is selected
                    if (actionMeta.action === 'input-change') {
                        setInputValue(value)
                    }
                }}
                placeholder="Skriv en kommando eller søk..."
                styles={{
                    valueContainer: base => ({ ...base, padding: 0 }),
                    input: base => ({ ...base, padding: 0, margin: 0 }),
                    indicatorSeparator: () => ({ display: 'none' }),
                    dropdownIndicator: () => ({ display: 'none' }),
                    group: base => ({ ...base, padding: 0 }),
                }}
                components={{
                    NoOptionsMessage: () => (
                        <div className="px-4 py-8 text-center">
                            <AstraParagraph variant="body" className="text-muted">
                                Ingen kommandoer funnet
                            </AstraParagraph>
                        </div>
                    ),
                    Menu: props => (
                        <div {...props.innerProps} className={`h-full w-full`} tabIndex={-1}>
                            {props.children}
                        </div>
                    ),
                    MenuList: ({ children, innerRef, innerProps, focusedOption }) => (
                        <div className="flex h-[400px] rounded-b-xl" tabIndex={-1}>
                            <div ref={innerRef} {...innerProps} className={`flex-grow overflow-auto p-2`} tabIndex={-1}>
                                {children}
                            </div>
                            <AnimatePresence>
                                {/** type from react-select is not correct here, will give runtime error if not checked for */}
                                {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
                                {focusedOption && focusedOption.focusPreviewRender && (
                                    <motion.div
                                        className="h-full w-[40%]"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        transition={{ ease: 'easeOut', duration: 0.12 }}
                                    >
                                        <div className="h-full overflow-auto border-l border-base">{focusedOption.focusPreviewRender(focusedOption)}</div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    ),

                    Option: ({ data, innerRef, innerProps, isFocused }) => (
                        <div ref={innerRef} {...innerProps} tabIndex={0}>
                            <div
                                className={astraTwMerge(
                                    'border-box flex cursor-pointer items-center justify-between gap-3 rounded-md border border-transparent px-2 py-2.5 transition-colors',
                                    isFocused ? 'border border-base bg-surface-muted shadow-[0px_1px_1px_-2px_#0000000D]' : ''
                                )}
                            >
                                {data.renderOption ? (
                                    data.renderOption()
                                ) : (
                                    <CommandPaletteOption icon={data.icon} label={data.label} description={data.description} />
                                )}
                            </div>
                        </div>
                    ),
                    GroupHeading: ({ children }) => (
                        <div className="p-1 py-1">
                            <span className="text-xs font-semibold uppercase tracking-wider text-muted">{children}</span>
                        </div>
                    ),
                    Control: ({ children, innerRef, innerProps }) => (
                        <div ref={innerRef} {...innerProps} className="flex items-center gap-3 border-b border-base p-4">
                            {children}
                        </div>
                    ),
                    Input: ({ innerRef, ...props }) => (
                        <components.Input
                            {...props}
                            innerRef={instance => {
                                if (!inputRef.current) {
                                    inputRef.current = instance
                                }
                                if (typeof innerRef === 'function') {
                                    innerRef(instance)
                                }
                            }}
                            autoFocus
                        />
                    ),
                }}
            />
            <div className="flex h-[53px] items-center justify-between gap-2 rounded-b-xl border-t border-base px-4 py-3">
                <div className="flex items-center gap-3 text-xs">
                    <div className="flex items-center gap-1">
                        <span className="rounded-md border border-base p-1">
                            <AstraIcon icon={IconArrowUp} />
                        </span>
                        <span className="rotate-180 transform rounded-md border border-base p-1">
                            <AstraIcon icon={IconArrowUp} />
                        </span>
                        <span>Naviger</span>
                    </div>
                </div>
                <AstraButton
                    variant="primary"
                    label="Åpne"
                    endIcon={
                        <span className="mt-0.5 inline-flex items-center justify-center rounded-md border border-base p-0.5 text-white">
                            <AstraIcon icon={IconArrowBackUp} />
                        </span>
                    }
                />
            </div>
        </>
    )
}
